'use client'

import type { Product } from '@/payload-types'

import { Media } from '@/components/Media'
import { Logo } from '@/components/brand/Logo'
import { useSearchParams } from 'next/navigation'
import React, { useEffect } from 'react'

import { cn } from '@/utilities/cn'
import { DefaultDocumentIDType } from 'payload'

type Props = {
  gallery: NonNullable<Product['gallery']>
}

/**
 * Галерея карточки: крупный кадр и лента превью под ним. Если в галерее
 * есть снимки, привязанные к варианту, при выборе цвета кадр меняется сам.
 */
export const Gallery: React.FC<Props> = ({ gallery }) => {
  const searchParams = useSearchParams()
  const [current, setCurrent] = React.useState(0)

  useEffect(() => {
    const values = Array.from(searchParams.values())
    if (!values.length) return

    const index = gallery.findIndex((item) => {
      if (!item.variantOption) return false
      let variantID: DefaultDocumentIDType
      if (typeof item.variantOption === 'object') variantID = item.variantOption.id
      else variantID = item.variantOption
      return Boolean(values.find((value) => value === String(variantID)))
    })
    if (index !== -1) setCurrent(index)
  }, [searchParams, gallery])

  const active = gallery[current]

  return (
    <div className="flex flex-col gap-4">
      <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-foam">
        {active?.image && typeof active.image === 'object' ? (
          <Media fill priority resource={active.image} imgClassName="object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-sun">
            <Logo variant="sun" title="" aria-hidden className="h-1/2 w-auto text-ink/25" />
          </div>
        )}
      </div>

      {gallery.length > 1 && (
        <ul className="flex gap-3 overflow-x-auto pb-1">
          {gallery.map((item, i) => {
            if (typeof item.image !== 'object' || !item.image) return null
            return (
              <li key={`${item.image.id}-${i}`}>
                <button
                  type="button"
                  onClick={() => setCurrent(i)}
                  aria-label={`Фото ${i + 1}`}
                  aria-current={i === current}
                  className={cn(
                    'relative h-20 w-16 overflow-hidden rounded-lg bg-foam transition-all md:h-24 md:w-20',
                    i === current ? 'ring-2 ring-ink' : 'opacity-70 hover:opacity-100',
                  )}
                >
                  <Media fill resource={item.image} imgClassName="object-cover" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
